import { maxAssetUrl, maxBentoArt, maxSlide } from './assets';
import { getMaxChipsForRegion, type MaxChipKey } from './features';

/**
 * Every row and section of the MAX explainer modal. One ordered model so the
 * bento count, the library sections and the "What you get" table can never
 * disagree about what a region gets.
 */
export type MaxLibraryKey =
  | 'jade'
  | 'uhx'
  | 'afib'
  | 'cardio'
  | 'cycle'
  | 'lesmills'
  | 'respiratory'
  | 'tesla'
  | 'migraine'
  | 'healthex';

export interface MaxLibraryEntry {
  key: MaxLibraryKey;
  /** Region gate, shared with the funnel chip row. `null` = always shown. */
  chip: MaxChipKey | null;
  /** Jade and UltrahumanX are MAX benefits, not PowerPlugs — excluded from "N PowerPlugs". */
  isPowerPlug: boolean;
  /** Ready-to-use `<img src>`; the modal renders plain images, not CustomImage. */
  orb: string;
  slides: { desktop: string[]; mobile: string[] };
}

/**
 * Figma ships 3 slides per PowerPlug, in matching desktop (704x420) and mobile
 * (345x460) crops — component sets `2359:98443` and `2359:104872`. UltrahumanX
 * is a bespoke card in the modal frame rather than a set variant, so it has one.
 */
const slides = (key: string, count = 3) => ({
  desktop: Array.from({ length: count }, (_, i) =>
    maxSlide('desktop', `${key}-${i + 1}.webp`),
  ),
  mobile: Array.from({ length: count }, (_, i) =>
    maxSlide('mobile', `${key}-${i + 1}.webp`),
  ),
});

export const MAX_LIBRARY: Record<MaxLibraryKey, MaxLibraryEntry> = {
  jade: {
    key: 'jade',
    chip: 'jade',
    isPowerPlug: false,
    orb: maxAssetUrl('chips/jade.png'),
    slides: slides('jade'),
  },
  uhx: {
    key: 'uhx',
    chip: null,
    isPowerPlug: false,
    // No `chips/uhx.png` on the CDN — this orb is the Figma export.
    orb: maxBentoArt('orb-uhx.webp'),
    slides: slides('uhx', 1),
  },
  afib: {
    key: 'afib',
    chip: 'afib',
    isPowerPlug: true,
    orb: maxAssetUrl('chips/afib.png'),
    slides: slides('afib'),
  },
  cardio: {
    key: 'cardio',
    chip: 'cardio',
    isPowerPlug: true,
    orb: maxAssetUrl('chips/cardio.png'),
    slides: slides('cardio'),
  },
  cycle: {
    key: 'cycle',
    chip: 'cycle',
    isPowerPlug: true,
    orb: maxAssetUrl('chips/cycle.png'),
    slides: slides('cycle'),
  },
  lesmills: {
    key: 'lesmills',
    chip: 'lesmills',
    isPowerPlug: true,
    orb: maxAssetUrl('chips/lesmills.png'),
    slides: slides('lesmills'),
  },
  respiratory: {
    key: 'respiratory',
    chip: 'respiratory',
    isPowerPlug: true,
    orb: maxAssetUrl('chips/respiratory.png'),
    slides: slides('respiratory'),
  },
  tesla: {
    key: 'tesla',
    chip: 'tesla',
    isPowerPlug: true,
    orb: maxAssetUrl('chips/tesla.png'),
    slides: slides('tesla'),
  },
  migraine: {
    key: 'migraine',
    chip: 'migraine',
    isPowerPlug: true,
    orb: maxAssetUrl('chips/migraine.png'),
    slides: slides('migraine'),
  },
  healthex: {
    key: 'healthex',
    chip: 'healthex',
    isPowerPlug: true,
    orb: maxAssetUrl('chips/healthex.png'),
    slides: slides('healthex'),
  },
};

/** Figma "Everything in MAX, in detail" order: the two benefits lead. */
const LIBRARY_ORDER: MaxLibraryKey[] = [
  'jade',
  'uhx',
  'afib',
  'cardio',
  'cycle',
  'respiratory',
  'healthex',
  'lesmills',
  'tesla',
  'migraine',
];

/** Figma "What you get" order: UltrahumanX closes the table instead. */
const ROW_ORDER: MaxLibraryKey[] = [
  'jade',
  'afib',
  'cardio',
  'cycle',
  'lesmills',
  'respiratory',
  'tesla',
  'migraine',
  'healthex',
  'uhx',
];

const visibleIn = (region: string | null | undefined) => {
  const chips = new Set<MaxChipKey>(getMaxChipsForRegion(region));
  return (key: MaxLibraryKey) => {
    const { chip } = MAX_LIBRARY[key];
    return chip === null || chips.has(chip);
  };
};

export const getMaxLibraryForRegion = (
  region?: string | null,
): MaxLibraryEntry[] =>
  LIBRARY_ORDER.filter(visibleIn(region)).map((key) => MAX_LIBRARY[key]);

export const getMaxRowsForRegion = (
  region?: string | null,
): MaxLibraryEntry[] =>
  ROW_ORDER.filter(visibleIn(region)).map((key) => MAX_LIBRARY[key]);
